/**
 *  External Modules
 */

import Vue from "vue";
import { createAuth0Client } from "@auth0/auth0-spa-js";
import { useStore } from "@/core/services/store";
import BaseAuth from "@/components/General/Auth/baseAuth";

/**
 *  Vue.js Instance Definition
 */

let instance;
const originalUrl = window.location.origin;

export const getAuth0 = () => instance;

/**
 *  Vue.js Instance Initialization
 */

export const useAuth0Plugin = ({
  onRedirectCallback = async () => {
    // Replace the state with a readable url
    window.history.replaceState(
      {},
      document.title,
      `${originalUrl}/#/login-callback`
    );
  },
  redirectUri = `${originalUrl}/#/login-callback`,
  ...pluginOptions
}) => {
  if (instance) return instance;

  instance = new Vue({
    data() {
      return {
        auth0Client: null,
        isLoading: true,
        isAuthenticated: false,
        user: {},
        error: null
      };
    },

    async created() {
      if (!BaseAuth.useAuth0()) {
        return;
      }

      this.auth0Client = await createAuth0Client({
        ...pluginOptions,
        domain: pluginOptions.domain,
        clientId: pluginOptions.clientId,
        cacheLocation: "localstorage",
        useRefreshTokens: true,
        authorizationParams: {
          audience: pluginOptions.audience,
          redirect_uri: redirectUri
        }
      });

      try {
        const search = window.location.search;

        if (
          (search.includes("code=") || search.includes("error=")) &&
          search.includes("state=")
        ) {
          const appState = await this.auth0Client.handleRedirectCallback();

          await onRedirectCallback(appState);
        }
      } catch (error) {
        this.error = error;
      } finally {
        this.isAuthenticated = await this.auth0Client.isAuthenticated();
        this.user = await this.auth0Client.getUser();

        this.isLoading = false;
      }
    },
    methods: {
      loginWithRedirect(options) {
        const completeOptions = this.getAuthParams(options);
        return this.auth0Client.loginWithRedirect(completeOptions);
      },

      logout(options) {
        let completeOptions = this.getAuthParams(options);
        const returnToUri = `${originalUrl}/#/login`;
        completeOptions = {
          ...completeOptions,
          logoutParams: {
            ...(completeOptions || {}),
            returnTo: returnToUri
          }
        };
        return this.auth0Client.logout(completeOptions);
      },

      getAccessTokenSilently(options) {
        const completeOptions = this.getAuthParams(options);
        return this.auth0Client.getTokenSilently(completeOptions);
      },

      getIdTokenClaims() {
        return this.auth0Client.getIdTokenClaims();
      },

      getUser() {
        return this.auth0Client.getUser();
      },

      getAuthParams(options) {
        const store = useStore();
        const orgId = store.getters["instance/instance"]?.organizationId;

        return {
          ...(options || {}),
          authorizationParams: {
            ...(options?.authorizationParams || {}),
            organization: orgId
          }
        };
      }
    }
  });

  return instance;
};

/**
 *  Vue.js Plugin Definition
 */

export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth0 = useAuth0Plugin(options);
  }
};
